import { skipToken } from "@reduxjs/toolkit/query";
import { useGetQuestionnaireSectionsQuery } from "../api/questionnaire";
import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import { QuestionnaireSectionStatus } from "../../../api/src/types";
import { useMemo } from "react";
import { formatUserFirstName } from "../utils/format";

interface UsePartnerQuestionnaireState {
  isLoading: boolean;
  isError: boolean;
  isPartnerQuestionnaireComplete: boolean;
  partnerId: string | null;
  partnerName: string | null;
  user: { id: string } | undefined;
}

/**
 * Hook to check if the partner's questionnaire is complete
 * @returns {UsePartnerQuestionnaireState}
 */
export const usePartnerQuestionnaireState =
  (): UsePartnerQuestionnaireState => {
    const {
      user,
      document,
      isLoading: isDocumentLoading,
      error: documentError,
    } = useGetCurrentUserDocument();

    const isUserA = document?.userAId === user?.id;
    const { partnerId, partnerName } = isUserA
      ? {
          partnerId: document?.userBId ?? null,
          partnerName: formatUserFirstName(document?.userB),
        }
      : {
          partnerId: document?.userAId ?? null,
          partnerName: formatUserFirstName(document?.userA),
        };

    const {
      data: partnerQuestionnaireSections,
      isLoading: isPartnerQuestionnaireLoading,
      isError: isPartnerQuestionnaireError,
    } = useGetQuestionnaireSectionsQuery(
      partnerId ? { id: partnerId, includeStatus: true } : skipToken,
      { refetchOnMountOrArgChange: true },
    );

    const isLoading = isDocumentLoading || isPartnerQuestionnaireLoading;
    const isError = !!documentError || isPartnerQuestionnaireError;

    const isPartnerQuestionnaireComplete = useMemo(() => {
      const completedStatuses = [
        QuestionnaireSectionStatus.COMPLETED,
        QuestionnaireSectionStatus.NOT_APPLICABLE,
      ];

      return !!partnerQuestionnaireSections?.every((section) =>
        completedStatuses.includes(section.status),
      );
    }, [partnerQuestionnaireSections]);

    return {
      isLoading,
      isError,
      isPartnerQuestionnaireComplete,
      partnerId,
      partnerName,
      user,
    };
  };
